/**
 * Shared Utility Functions
 */

// Auth/token
export const getToken = () => localStorage.getItem("cpi_token");
export const setToken = (t) => t ? localStorage.setItem("cpi_token", t) : localStorage.removeItem("cpi_token");

// Fetch a short-lived token (60s) for EventSource/SSE. The call goes through the Bearer
// header, so the session token never ends up in the URL, only this short-lived token,
// which expires quickly.
export const getSseToken = async () => {
  const t = getToken();
  if (!t) return null;
  try {
    const r = await fetch("/api/auth/sse-token", { method: "POST", headers: { Authorization: `Bearer ${t}` } });
    if (!r.ok) return null;
    return (await r.json()).token || null;
  } catch { return null; }
};

// API client
// opts.timeout: milliseconds until AbortController.abort() fires. Default 30000.
//   Set opts.timeout higher (or to 0) for long-running endpoints, such as
//   /api/cicd/transport/snapshot, which is called with a 180000ms timeout
//   because it downloads and commits every selected artifact.
export const api = async (url, opts = {}) => {
  const t = getToken();
  const method = (opts.method || "GET").toUpperCase();
  const maxRetries = method === "GET" ? 3 : 0;
  const timeoutMs = typeof opts.timeout === "number" ? opts.timeout : 30000;
  const fetchOpts = { ...opts };
  delete fetchOpts.timeout;
  // Capture the caller's signal so fetch can be aborted cleanly
  const userSignal = fetchOpts.signal;
  delete fetchOpts.signal;
  for (let attempt = 0; attempt <= maxRetries; attempt++) {
    const controller = new AbortController();
    const timer = timeoutMs > 0 ? setTimeout(() => controller.abort(), timeoutMs) : null;
    if (userSignal) {
      if (userSignal.aborted) { if (timer) clearTimeout(timer); throw new DOMException("Aborted", "AbortError"); }
      userSignal.addEventListener("abort", () => controller.abort(), { once: true });
    }
    try {
      const r = await fetch(url, {
        headers: {
          "Content-Type": "application/json",
          // Carries the UI language for anything that wants to read it. Server
          // messages are English at the source and do not depend on this header.
          "Accept-Language": (typeof window !== "undefined" && window.__iioLang === "en") ? "en" : "de",
          ...(t ? { Authorization: `Bearer ${t}` } : {}),
        },
        signal: controller.signal,
        ...fetchOpts,
      });
      if (timer) clearTimeout(timer);
      if (r.status === 401) { setToken(null); window.location.reload(); return; }
      if (method === "GET" && [502, 503, 504].includes(r.status) && attempt < maxRetries) {
        await new Promise(res => setTimeout(res, 1000 * Math.pow(2, attempt)));
        continue;
      }
      if (!r.ok) {
        const e = await r.json().catch(() => ({}));
        const base = e.error || `HTTP ${r.status}`;
        const msg = e.hint ? `${base} - ${e.hint}` : base;
        const err = new Error(msg);
        err.status = r.status;
        err.details = e.details;
        err.hint = e.hint;
        // Pass through the backend's business-level reason (e.g. a tenant rejection),
        // so the UI does not have to show raw technical text.
        err.reason = e.reason;
        // Machine-readable code from the backend. Matching on the message text does not work: it
        // comes back in German or English depending on Accept-Language.
        err.code = e.code;
        // Remaining response fields, so an endpoint can add details (e.g. which runs are
        // blocking an action) without changing this helper.
        err.body = e;
        err.unavailable = e.unavailable;
        throw err;
      }
      return r.json();
    } catch (err) {
      if (timer) clearTimeout(timer);
      if (err.name === "AbortError" && attempt < maxRetries) {
        await new Promise(res => setTimeout(res, 1000 * Math.pow(2, attempt)));
        continue;
      }
      throw err;
    }
  }
};

// CSS variable accessor
export const v = (name) => `var(--${name})`;

// OData date parsing
export const parseODataDate = (d) => {
  if (typeof d !== 'string') return 0;
  const m = d.match(/\/Date\((\d+)\)\//);
  return m ? parseInt(m[1]) : 0;
};

// Format date
export const formatDate = (d) => {
  const t = parseODataDate(d);
  return t
    ? new Intl.DateTimeFormat("de-DE", {
        day: "2-digit", month: "2-digit", year: "numeric",
        hour: "2-digit", minute: "2-digit", second: "2-digit",
      }).format(new Date(t))
    : "-";
};

// Format duration
export const formatDuration = (start, end) => {
  const ms = parseODataDate(end) - parseODataDate(start);
  if (ms < 1000) return `${ms}ms`;
  if (ms < 60000) return `${(ms / 1000).toFixed(1)}s`;
  return `${(ms / 60000).toFixed(1)}m`;
};

// Tenant URL to suite URL
export const toSuiteUrl = (tenantUrl, suiteUrl) => {
  if (suiteUrl) return suiteUrl.replace(/\/+$/, "");
  if (!tenantUrl) return "";
  return tenantUrl.replace(/\.it-[a-z0-9-]+\.cfapps/, ".integrationsuite.cfapps");
};

// Monitoring deep link
export const monitoringLink = (baseUrl, guid) => {
  const g = typeof guid === "string" ? guid : guid?.MessageGuid || "";
  return `${baseUrl}/shell/monitoring/Messages/${encodeURIComponent(JSON.stringify({ identifier: g }))}`;
};
